import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useAuth } from "../providers/AuthProvider";
import { useLocation, useNavigate } from 'react-router-dom';
import EnhancedPostComposer from "../components/EnhancedPostComposer";
import { Container } from "../base";
import Typography from "@mui/material/Typography";
import { Alert, Box, Chip } from "@mui/material";
import { Edit as EditIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import TwitterConnectButtonSecure from "../components/TwitterConnectButtonSecure";
import LinkedInConnectButton from "../components/LinkedInConnectButton";
import Grid from "@mui/material/Grid";
import ThreadsConnectButton from "../components/ThreadsConnectButton";
import BlueSkyConnectButton from "../components/BlueSkyConnectButton";
import TikTokConnectButton from "../components/TikTokConnectButton";
import MastodonConnectButton from "../components/MastodonConnectButton";
import FacebookConnectButton from "../components/FacebookConnectButton";
import LinkedInOrgConnectButton from "../components/LinkedInOrgConnectButton";
import InstagramConnectButton from "../components/InstagramConnectButton";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTracking } from '../hooks/useTracking';
import { CircularProgress } from '@mui/material';

dayjs.extend(relativeTime);

// const Container = styled.div`
//   display: flex;
//   flex-wrap: wrap;
// `;

const TextField = styled.input`
  margin-left: 8px;
  margin-right: 8px;
  // width: 200px; // Uncomment if needed
`;

const Button = styled.button`
  margin: 8px;
`;
// Styled component that properly forwards all props
const StyledComposePost = styled(EnhancedPostComposer)`
  width: 70vw;
  display: flex;
  align-self: center;
  flex-direction: column;
`;

const MessageWrapper = styled('div')(({theme}) => ({
    margin: theme.spacing(2),
    padding: theme.spacing(5),
    backgroundColor: theme.palette.grey["80"],
    color: theme.palette.grey["100"],
    display: 'flex',
    borderRadius: theme.spacing(2),
    // height: '80vh',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
}));

const EditModeHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
}));

const TimeComparisonBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
}));

function NewPostPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { trackEvent } = useTracking();
    const { user } = useAuth();
    
    // Debug the location state
    console.log('[NewPost] Location state:', location.state);
    console.log('[NewPost] Edit mode:', location.state?.editMode);
    console.log('[NewPost] Post data:', location.state?.postData);
    
    // Extract edit mode and post data from navigation state
    const editMode = location.state?.editMode || false;
    const postData = location.state?.postData || null;
    const rescheduleOnly = location.state?.rescheduleOnly || false;
    
    // Validate post data structure
    if (editMode && postData) {
        console.log('[NewPost] Validating post data structure:', {
            hasId: !!postData.id,
            hasPostText: !!postData.post_text,
            hasPlatformDetails: !!postData.platform_details,
            platformDetailsLength: postData.platform_details?.length,
            hasScheduledDate: !!postData.scheduled_post_date,
            hasImageUrl: !!postData.img_url
        });
    }
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Debug user auth state
    console.log('[NewPost] User loaded:', !!user);
    console.log('[NewPost] User array:', user);
    console.log('[NewPost] User[0]:', user[0]);
    
    useEffect(() => {
        // Log when component mounts
        console.log('[NewPost] Component mounted with editMode:', editMode, 'postData:', postData);
        
        // Check if user is authenticated
        if (!user || user.length === 0) {
            console.log('[NewPost] No user found, still loading...');
        } else {
            console.log('[NewPost] User authenticated:', user[0]);
            setLoading(false);
        }
    }, [user, editMode, postData]);
    
    // Track page view
    useEffect(() => {
        if (editMode) {
            trackEvent('edit_post_page_viewed', {
                post_id: postData?.id,
                reschedule_only: rescheduleOnly,
            });
        } else {
            trackEvent('new_post_page_viewed');
        }
    }, [editMode, postData, rescheduleOnly, trackEvent]);
    
    // Show loading state while auth is loading
    if (!user || user.length === 0) {
        console.log('[NewPost] Rendering loading state...');
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }
    
    // Calculate edit restrictions for scheduled posts
    const getEditRestrictions = () => {
        if (!editMode || !postData?.scheduled_post_date) {
            return { readOnly: false, limitedMode: false };
        }
        
        const now = dayjs();
        const scheduledTime = dayjs(postData.scheduled_post_date);
        const minutesUntilPost = scheduledTime.diff(now, 'minute');
        
        console.log('[NewPost] Minutes until post:', minutesUntilPost);
        
        return {
            readOnly: minutesUntilPost < 5,
            limitedMode: minutesUntilPost < 15,
            minutesUntilPost
        };
    };
    
    const { readOnly, limitedMode, minutesUntilPost } = getEditRestrictions();
    
    console.log('[NewPost] Edit restrictions:', { readOnly, limitedMode, minutesUntilPost });

    // Format scheduled time display
    const formatScheduledTime = (dateString) => {
        if (!dateString) return '';
        return dayjs(dateString).format('MMM D, YYYY at h:mm A');
    };

    return (
        <Container>
            {!user[0]?.userprofile?.social_accounts || user[0].userprofile.social_accounts.length <= 0 ? (
                <>
                <MessageWrapper>
                    <Typography variant="h3" align="center" mb={3}>
                        Ready to draft your first post?
                    </Typography>
                    <Typography variant="h5" align="center" mb={3}>
                        Connect to one of your social media accounts to start drafting a new post.
                    </Typography>
                    <Grid container display="flex" flexDirection="row" align="center" gap={3} justifyContent="center">
                        <TwitterConnectButtonSecure/>
                        <LinkedInConnectButton/>
                        <LinkedInOrgConnectButton />
                        <BlueSkyConnectButton />
                        <ThreadsConnectButton />
                        <InstagramConnectButton />
                        <FacebookConnectButton />
                        <TikTokConnectButton />
                        <MastodonConnectButton />
                    </Grid>
                </MessageWrapper>
                </>
            ) : (
                <>
                    {/* Edit Mode Header */}
                    {editMode && (
                        <EditModeHeader>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <EditIcon />
                                <Typography variant="h6">
                                    Editing Scheduled Post
                                </Typography>
                                <Chip 
                                    icon={<ScheduleIcon />} 
                                    label={minutesUntilPost ? minutesUntilPost + ' minutes' : 'Calculating...'} 
                                    size="small"
                                    color={readOnly ? "error" : limitedMode ? "warning" : "primary"}
                                />
                            </Box>
                            {postData?.id && (
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Post ID: {postData.id}
                                </Typography>
                            )}
                        </EditModeHeader>
                    )}
                    
                    {/* Edit Restrictions Alert */}
                    {readOnly && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            This post is scheduled to publish in {minutesUntilPost} minutes and cannot be edited. 
                            Please wait until after it publishes to make changes.
                        </Alert>
                    )}
                    
                    {limitedMode && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            This post is scheduled to publish in {minutesUntilPost} minutes. 
                            You can only edit the content and scheduling. Platform changes are disabled.
                        </Alert>
                    )}
                    
                    {/* Time Comparison for Edit Mode */}
                    {editMode && postData?.scheduled_post_date && !readOnly && !limitedMode && (
                        <TimeComparisonBox>
                            <Typography variant="subtitle2" gutterBottom>
                                Schedule Time Comparison
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Current Schedule:
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatScheduledTime(postData.scheduled_post_date)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        New Schedule:
                                    </Typography>
                                    <Typography variant="body1" id="new-schedule-display">
                                        Will update when you select a new time
                                    </Typography>
                                </Grid>
                            </Grid>
                        </TimeComparisonBox>
                    )}
                    
                    <Typography variant="h2" align="center" mb={3}>
                        {editMode ? 'Edit Post' : 'Create a Post'}
                    </Typography>
                    
                    <StyledComposePost
                        editMode={editMode}
                        postData={postData}
                        readOnly={readOnly}
                        limitedMode={limitedMode}
                        rescheduleOnly={rescheduleOnly}
                        onScheduleUpdate={(newTime) => {
                            // Update the new schedule display
                            const display = document.getElementById('new-schedule-display');
                            if (display && newTime) {
                                display.textContent = formatScheduledTime(newTime);
                            }
                        }}
                    />
                </>
            )}
        </Container>
    );
}

export default NewPostPage;